/**
 * useT — internationalisation hook returning a stable translation callback.
 *
 * The returned `t(key, vars?)` function:
 *   1. Looks up `key` in the active locale catalogue (from I18nContext).
 *   2. Falls back to the English fallback catalogue (always loaded).
 *   3. Falls back to the key itself if neither catalogue has the entry.
 *   4. Substitutes `{var}` placeholders with values from the `vars` object.
 *
 * When the `i18n-enabled` feature flag is off (production default), the
 * provider mounts with the inlined en-US catalogue, so `t(key)` returns the
 * English string synchronously with zero async overhead.
 *
 * Source contract: specs/prd.md FR-023.
 */
import { useCallback, useContext } from "react";
import { I18nContext } from "./I18nProvider";

/** Variable substitution map for `{var}` placeholders. */
export type TVars = Record<string, string | number>;

/** The translation function signature. */
export type TFunction = (key: string, vars?: TVars) => string;

/**
 * Returns a stable `t()` function that resolves i18n keys against the active
 * locale catalogue with English fallback.
 *
 * Must be called inside `<I18nProvider>`. Safe to call in any component body
 * (unconditional — no Rules-of-Hooks violation).
 */
export function useT(): TFunction {
  const ctx = useContext(I18nContext);

  return useCallback(
    (key: string, vars?: TVars): string => {
      // 1. Active locale catalogue
      let value = ctx?.catalogue?.[key];

      // 2. English fallback catalogue
      if (!value) {
        value = ctx?.fallback?.[key];
      }

      // 3. Key itself as ultimate fallback
      if (!value) {
        return substituteVars(key, vars);
      }

      return substituteVars(value, vars);
    },
    [ctx],
  );
}

/**
 * Substitutes `{var}` placeholders in a string with values from `vars`.
 *
 * @example
 *   substituteVars("Hello {name}", { name: "World" }) // "Hello World"
 */
function substituteVars(
  template: string,
  vars: TVars | undefined,
): string {
  if (!vars) return template;

  let result = template;
  for (const [varName, varValue] of Object.entries(vars)) {
    result = result.replaceAll(`{${varName}}`, String(varValue));
  }
  return result;
}
